import type { DeviceGroup, Organization, Site, Uuid } from '@/lib/api/types';

/*
  The parent rules of the tenancy hierarchy, in one place.

  docs/07-identity-rbac.md I.2:

      tenant -> organization -> site -> device_group -> device

  docs/04-data-model.md F.3 adds the detail that matters to a form: device_groups carries
  BOTH organization_id (required) and site_id (nullable, where null means an organization
  wide group). That redundancy is deliberate per F.1, and it creates exactly one way to make
  an inconsistent record: a group whose site belongs to a different organization than the
  group's own. No database constraint catches it, because both columns are individually
  valid foreign keys.

  So the rule is enforced in the only place it can be, which is by never offering the
  inconsistent combination. Every form that picks a parent goes through the functions below
  rather than filtering a list inline, because a second inline filter is where the two copies
  of the rule drift apart.

  The same functions drive the device move dialog, since a device carries the same three
  keys and can be made inconsistent in the same way.
*/

export function sitesForOrganization(
  sites: readonly Site[],
  organizationId: Uuid | null,
): readonly Site[] {
  if (organizationId === null) {
    return [];
  }
  return [...sites]
    .filter((site) => site.organizationId === organizationId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Groups a device may be placed into, given its organization and site.
 *
 * Two kinds qualify and no others:
 *
 *   * a group in the same organization with site_id null, which is organization wide and
 *     therefore valid at any site inside that organization;
 *   * a group in the same organization whose site_id is exactly the selected site.
 *
 * A group belonging to a different site of the same organization is excluded. It would be a
 * valid foreign key and a wrong record: the device would appear in a group that claims to be
 * at a site the device is not at, and a role assignment scoped to that group would grant
 * access to a machine at another location.
 */
export function groupsForPlacement(
  groups: readonly DeviceGroup[],
  organizationId: Uuid | null,
  siteId: Uuid | null,
): readonly DeviceGroup[] {
  if (organizationId === null) {
    return [];
  }
  return [...groups]
    .filter(
      (group) =>
        group.organizationId === organizationId &&
        (group.siteId === null || group.siteId === siteId),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Every group under an organization, regardless of site. For listings, not for pickers. */
export function groupsForOrganization(
  groups: readonly DeviceGroup[],
  organizationId: Uuid,
): readonly DeviceGroup[] {
  return [...groups]
    .filter((group) => group.organizationId === organizationId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * True when the three keys are mutually consistent. Used as a last check before submit, so a
 * stale list in the picker cannot produce a bad request even if the options were computed
 * before a site moved.
 */
export function isPlacementConsistent(
  sites: readonly Site[],
  groups: readonly DeviceGroup[],
  placement: {
    readonly organizationId: Uuid;
    readonly siteId: Uuid | null;
    readonly deviceGroupId: Uuid | null;
  },
): boolean {
  if (placement.siteId !== null) {
    const site = sites.find((candidate) => candidate.id === placement.siteId);
    if (site?.organizationId !== placement.organizationId) {
      return false;
    }
  }
  if (placement.deviceGroupId !== null) {
    const group = groups.find((candidate) => candidate.id === placement.deviceGroupId);
    if (group === undefined) {
      return false;
    }
    if (group.organizationId !== placement.organizationId) {
      return false;
    }
    if (group.siteId !== null && group.siteId !== placement.siteId) {
      return false;
    }
  }
  return true;
}

/** "Acme Dental / Main Clinic / Operatory Workstations", skipping the parts that are null. */
export function describePlacement(
  organizations: readonly Organization[],
  sites: readonly Site[],
  groups: readonly DeviceGroup[],
  placement: {
    readonly organizationId: Uuid;
    readonly siteId: Uuid | null;
    readonly deviceGroupId: Uuid | null;
  },
): string {
  const parts = [
    organizations.find((org) => org.id === placement.organizationId)?.name ?? 'Unknown organization',
    placement.siteId === null
      ? 'no site'
      : (sites.find((site) => site.id === placement.siteId)?.name ?? 'unknown site'),
    placement.deviceGroupId === null
      ? 'no group'
      : (groups.find((group) => group.id === placement.deviceGroupId)?.name ?? 'unknown group'),
  ];
  return parts.join(' / ');
}

/** A slug suggestion from a display name. Matches the pattern the API validates against. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    // Strips the combining marks NFKD just split off, so an accented name slugs to its
    // base letters rather than to a hyphen where the accent was. p{M} is the Unicode
    // mark category, which is more honest than hardcoding a code point range.
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '');
}
